import { configureAuth } from '@lastbrand/fe-common/universal-auth'

import getAmplifyConfig from '@src/modules/auth/amplify/amplify-config'
import type { IdentityConfigApiResponse } from '@src/modules/auth/identity-config-api'
import {
  fetchIdentityConfigByEmail,
  fetchIdentityConfigByRegion,
  mapIdentityConfigToAuthConfig,
} from '@src/modules/auth/identity-config-api'
import { readIdentityConfigCache, recordIdentityConfigFetch } from '@src/modules/auth/identity-config-cache'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { GATSBY_USER_STORAGE_KEY } from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { getStorage } from '@src/utils/enhancedStorage'

/** When set (e.g. after login), identity API returns the user's home Cognito pool — not only the browse-region default. */
function getEmailHintForPoolResolution(): string | undefined {
  const raw = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)?.trim()
  if (raw) {
    return raw.toLowerCase()
  }
  try {
    const gatsbyUser = getStorage().getItem(GATSBY_USER_STORAGE_KEY, { isGlobal: true })
    if (gatsbyUser) {
      const parsed = JSON.parse(gatsbyUser) as { email?: string }
      if (parsed.email) {
        return parsed.email.trim().toLowerCase()
      }
    }
  } catch {
    // ignore parse errors
  }
  return undefined
}

async function fetchIdentityConfigForRegion(regionId: number): Promise<IdentityConfigApiResponse> {
  const email = getEmailHintForPoolResolution()
  const cached = readIdentityConfigCache(regionId, email)
  if (cached) {
    recordIdentityConfigFetch(regionId, email, cached)
    return cached
  }

  let apiResponse: IdentityConfigApiResponse
  if (email) {
    try {
      apiResponse = await fetchIdentityConfigByEmail(regionId, email)
    } catch (emailErr) {
      captureException(emailErr as Error, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        extraInfo: { regionId, email, context: 'IDENTITY_CONFIG_BY_EMAIL' },
      })
      apiResponse = await fetchIdentityConfigByRegion(regionId)
    }
  } else {
    apiResponse = await fetchIdentityConfigByRegion(regionId)
  }
  recordIdentityConfigFetch(regionId, email, apiResponse)
  return apiResponse
}

const makeAuthConfigDataHolder = () => {
  let lastRegionId: number | null = null
  let initPromise: Promise<void> = Promise.resolve()

  const onError = (error: Error, context: Record<string, unknown>) => {
    captureException(error, {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      extraInfo: context,
    })
  }

  async function _doInitialize(regionId: number): Promise<void> {
    try {
      const apiResponse = await fetchIdentityConfigForRegion(regionId)
      await configureAuth({ ...mapIdentityConfigToAuthConfig(apiResponse), onError })
    } catch (err) {
      captureException(err as Error, {
        module: MODULES.UNIVERSAL_AUTH,
        feature: FEATURES.UNIVERSAL_AUTH,
        extraInfo: { regionId, context: 'IDENTITY_CONFIG_BY_REGION' },
      })
      const staticConfig = getAmplifyConfig()
      if (staticConfig) {
        await configureAuth({ ...staticConfig, onError })
      }
    }
  }

  return Object.freeze({
    initialize(regionId: number): Promise<void> {
      if (lastRegionId !== regionId) {
        lastRegionId = regionId
        initPromise = _doInitialize(regionId)
      }
      return initPromise
    },
  })
}

const authConfigDataHolder = makeAuthConfigDataHolder()

export default authConfigDataHolder
